import { BaseApiClient } from "../api/baseApiClient.service";

export interface TrialCoinWindow {
	used: number;
	limit: number;
	/** Null when nothing was spent in the window, i.e. no countdown to show. */
	windowResetAt: string | null;
}

/** Stable error codes that `/stripe/end-trial-now` may surface. */
export type EndTrialNowErrorCode =
	| "NO_PAYMENT_METHOD"
	| "NOT_TRIALING"
	| "STRIPE_ERROR"
	| "UNKNOWN";

export class EndTrialNowError extends Error {
	code: EndTrialNowErrorCode;
	constructor(code: EndTrialNowErrorCode, message: string) {
		super(message);
		this.name = "EndTrialNowError";
		this.code = code;
	}
}

/**
 * Trial-related read endpoints for the authenticated user.
 *
 * The cap-hit modal and the topbar window indicator both read from this so
 * they cannot drift from the backend cap helper.
 */
export class TrialService {
	private static baseApi = BaseApiClient.getInstance();

	static async getCoinWindow(): Promise<TrialCoinWindow> {
		return TrialService.baseApi.get<TrialCoinWindow>("trial/coin-window");
	}

	/**
	 * End the user's Stripe trial immediately and flip the subscription to
	 * paid. Throws a typed `EndTrialNowError` so callers can show the right
	 * recovery toast (e.g. prompt for a payment method).
	 */
	static async endTrialNow(): Promise<{ success: boolean }> {
		try {
			return await TrialService.baseApi.post<
				{ success: boolean },
				Record<string, never>
			>("stripe/end-trial-now", {});
		} catch (err) {
			const data = (err as { response?: { data?: unknown } })?.response?.data as
				| { code?: string; error?: string }
				| undefined;
			const code: EndTrialNowErrorCode =
				data?.code === "NO_PAYMENT_METHOD" ||
				data?.code === "NOT_TRIALING" ||
				data?.code === "STRIPE_ERROR"
					? data.code
					: "UNKNOWN";
			throw new EndTrialNowError(
				code,
				data?.error ?? (err instanceof Error ? err.message : "Unknown error"),
			);
		}
	}

	static async switchTrialToStarter(): Promise<{ success: boolean }> {
		return TrialService.baseApi.post<
			{ success: boolean },
			Record<string, never>
		>("stripe/switch-trial-to-starter", {});
	}
}
